import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { todayIso, fmtDateDayMonth, fmtWeekdayShort, fmtTime } from '../utils/dateUtils';
import useIsMobile from '../hooks/useIsMobile';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtMoney = (n) =>
  '₹\u202f' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');

const pctChange = (today, yesterday) => {
  if (!yesterday || yesterday === 0) return null;
  return ((today - yesterday) / yesterday) * 100;
};

const ORDER_TYPE_LABEL = {
  DINE_IN: 'Dine-In', TAKE_AWAY: 'Take Away', TAKEAWAY: 'Take Away',
  IN_ROOM_DINING: 'In-Room Dining', ROOM_SERVICE: 'Room Service',
  DELIVERY: 'Delivery', BAR: 'Bar'
};

const PAYMENT_COLOURS = {
  Cash: '#22c55e', Card: '#3b82f6', UPI: '#a855f7',
  Online: '#06b6d4', 'Room Charge': '#f59e0b', Unknown: 'var(--theme-text-muted)'
};

const ORDER_TYPE_COLOURS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#06b6d4', '#ef4444', '#ec4899'
];

const KOT_STATUS_COLOUR = { RAISED: '#f59e0b', PREPARING: '#3b82f6', READY: '#22c55e' };
const KOT_STATUS_LABEL = { RAISED: 'Raised', PREPARING: 'Preparing', READY: 'Ready to Serve' };

// ── Shared style primitives ───────────────────────────────────────────────────
const C = {
  bg: 'var(--theme-border-light)', cardBg: '#ffffff', border: 'var(--theme-border)',
  primary: '#1e3a5f', accent: '#3b82f6', success: '#22c55e',
  warn: '#f59e0b', danger: '#ef4444', text: '#1e293b', textMuted: 'var(--theme-text-secondary)',
  headerBg: '#1e3a5f', headerText: '#ffffff'
};

const card = {
  background: C.cardBg, border: `1px solid ${C.border}`,
  borderRadius: '12px', padding: '1rem 1.2rem',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
};

const secHead = {
  margin: '0 0 0.75rem', fontSize: '0.88rem', fontWeight: 700,
  color: C.primary, paddingBottom: '0.4rem', borderBottom: `2px solid ${C.border}`,
  textTransform: 'uppercase', letterSpacing: '0.04em'
};

// ── Mini bar chart ────────────────────────────────────────────────────────────
function MiniBars({ rows, valueKey, labelKey, colour = C.accent, height = 80 }) {
  const max = Math.max(...rows.map((r) => Number(r[valueKey] || 0)), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: `${height}px` }}>
      {rows.map((row, i) => {
        const val = Number(row[valueKey] || 0);
        const h = Math.max(2, Math.round((val / max) * (height - 14)));
        const c = typeof colour === 'function' ? colour(row, i) : colour;
        return (
          <div key={i} style={{ flex: 1, minWidth: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
            <div title={`${row[labelKey]}: ${fmtNum(val)}`}
              style={{ width: '100%', height: `${h}px`, background: c, borderRadius: '3px 3px 0 0', cursor: 'default', transition: 'height 0.3s' }} />
            {row[labelKey] !== undefined && (
              <div style={{ fontSize: '0.5rem', color: C.textMuted, textAlign: 'center', lineHeight: 1 }}>
                {String(row[labelKey]).slice(0, 5)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, pct, colour = C.accent, icon }) {
  const { isMobile } = useIsMobile();
  const pctPos = pct !== null && pct !== undefined && pct >= 0;
  return (
    <div style={{ ...card, borderTop: `4px solid ${colour}`, minWidth: isMobile ? 0 : '140px', padding: isMobile ? '0.75rem' : card.padding }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '0.75rem', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        {icon && <span style={{ fontSize: '1.1rem' }}>{icon}</span>}
      </div>
      <div style={{ fontSize: '1.45rem', fontWeight: 800, color: C.text, margin: '0.3rem 0 0.2rem' }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: C.textMuted }}>{sub}</div>}
      {pct !== null && pct !== undefined && (
        <div style={{ fontSize: '0.72rem', fontWeight: 600, color: pctPos ? C.success : C.danger, marginTop: '0.2rem' }}>
          {pctPos ? '▲' : '▼'} {Math.abs(pct).toFixed(1)}% vs yesterday
        </div>
      )}
    </div>
  );
}

// ── Main KotDashboard ─────────────────────────────────────────────────────────
function KotDashboard({ token, selectedProperty, selectedOutletId }) {
  const { isMobile } = useIsMobile();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [histView, setHistView] = useState('28d');
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState(selectedOutletId || '');
  const [lastRefresh, setLastRefresh] = useState(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(outletId ? { 'x-outlet-id': outletId } : {})
  }), [token, selectedProperty, outletId]);

  // Load outlets for selector
  useEffect(() => {
    if (!selectedProperty?.id) return;
    fetch(`/api/v1/core/outlets?property_id=${selectedProperty.id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-chain-id': selectedProperty?.chain_id || '',
        'x-property-id': selectedProperty?.id || ''
      }
    })
      .then(r => r.ok ? r.json() : [])
      .then(rows => {
        const all = (rows || []);
        const filtered = all.filter(o => String(o.outlet_type || '').toUpperCase() !== 'BAR' || o.module_kot_enabled === true);
        setOutlets(filtered);
      })
      .catch(() => {});
  }, [selectedProperty?.id, token, selectedProperty?.chain_id]);

  const loadStats = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const qs = outletId ? `?outlet_id=${encodeURIComponent(outletId)}` : '';
      const res = await fetch(`/api/v1/kot/dashboard${qs}`, { headers: headers() });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || res.statusText); }
      const data = await res.json();
      setStats(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty?.id, outletId, headers]);

  useEffect(() => { loadStats(); }, [loadStats]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    const t = setInterval(loadStats, 120000);
    return () => clearInterval(t);
  }, [loadStats]);

  // ── Derived values ─────────────────────────────────────────────────────────
  const history = stats?.history || [];
  const displayHistory = useMemo(() => {
    if (!stats) return [];
    return histView === '7d' ? history.slice(-7) : history;
  }, [stats, history, histView]);

  if (!stats && !loading && !error) {
    return <div style={{ padding: '2rem', color: C.textMuted }}>Select a property to load the KOT & POS Dashboard.</div>;
  }

  const todayRev = Number(stats?.todayRevenue || 0);
  const yesterdayRev = Number(stats?.yesterdayRevenue || 0);
  const mtdRev = Number(stats?.mtdRevenue || 0);
  const todayCount = Number(stats?.todayOrderCount || 0);
  const avgOrderVal = todayCount > 0 ? todayRev / todayCount : 0;
  const dayPct = pctChange(todayRev, yesterdayRev);

  const bsMap = stats?.billStatusBreakdown || {};
  const settledCount = bsMap['SETTLED']?.count || 0;
  const settledAmt = bsMap['SETTLED']?.amount || 0;
  const openCount = bsMap['OPEN']?.count || 0;
  const voidAna = stats?.voidAnalytics || {};
  const voidCount = Number(voidAna.voidCount || 0) || ((bsMap['VOID']?.count || 0) + (bsMap['VOIDED']?.count || 0));
  const voidRate = todayCount > 0 ? ((voidCount / todayCount) * 100).toFixed(1) : '0.0';

  const activeKots = stats?.activeKots || { RAISED: 0, PREPARING: 0, READY: 0 };
  const totalActiveKots = (activeKots.RAISED || 0) + (activeKots.PREPARING || 0) + (activeKots.READY || 0);

  const hourly = stats?.hourlyToday || [];
  const topItems = stats?.topItemsToday || [];
  const orderTypeMix = stats?.orderTypeMix || [];
  const paymentMix = stats?.paymentMix || [];
  const stationPerf = stats?.stationPerformance || [];
  const tableUtil = stats?.tableUtilization || [];
  const catMix = stats?.categorySalesMix || [];
  const disc = stats?.discountAnalytics || {};
  const forecast = stats?.forecast || {};

  const maxHourlyCount = Math.max(...hourly.map(r => r.order_count), 1);
  const maxHistAmt = Math.max(...displayHistory.map(r => Number(r.amount || 0)), 1);
  const payTotal = paymentMix.reduce((s, r) => s + Number(r.amount || 0), 0);
  const orderTypeTotal = orderTypeMix.reduce((s, r) => s + Number(r.cnt || 0), 0);
  const catTotal = catMix.reduce((s, r) => s + Number(r.total_amount || 0), 0);

  return (
    <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', background: C.bg, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: C.primary, fontWeight: 800, fontSize: '1.4rem' }}>KOT & POS Dashboard</h2>
          {lastRefresh && <div style={{ fontSize: '0.72rem', color: C.textMuted, marginTop: '0.2rem' }}>Last updated: {fmtTime(lastRefresh)}</div>}
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {outlets.length > 0 && (
            <select value={outletId} onChange={e => setOutletId(e.target.value)}
              style={{ padding: '0.5rem 0.75rem', borderRadius: '8px', border: `1px solid ${C.border}`, fontSize: '0.85rem', background: '#fff', color: C.text }}>
              <option value="">All Outlets</option>
              {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
          <button onClick={loadStats} disabled={loading} className="btn-primary"
            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(155px, 1fr))', gap: '0.85rem', marginBottom: '1.5rem' }}>
        <KpiCard label="Today Revenue" value={fmtMoney(todayRev)} sub={`${fmtNum(todayCount)} orders`} pct={dayPct} colour={C.accent} icon="💰" />
        <KpiCard label="MTD Revenue" value={fmtMoney(mtdRev)} sub="Month to date" colour="#8b5cf6" icon="📅" />
        <KpiCard label="Avg Order Value" value={fmtMoney(avgOrderVal)} sub={`${settledCount} settled`} colour={C.success} icon="🧾" />
        <KpiCard label="Active KOTs" value={fmtNum(totalActiveKots)} sub={`${activeKots.RAISED} raised · ${activeKots.PREPARING} cooking · ${activeKots.READY} ready`} colour={C.warn} icon="🍳" />
        <KpiCard label="Open Orders" value={fmtNum(openCount)} sub="Pending settlement" colour="#06b6d4" icon="🔓" />
        <KpiCard label="Void Rate" value={`${voidRate}%`} sub={`${voidCount} void · ${fmtMoney(voidAna.voidAmount)} lost`} colour={C.danger} icon="🚫" />
        <KpiCard label="Discounts Given" value={fmtMoney(disc.totalDiscount)} sub={`${disc.discountedOrders || 0} orders · avg ${disc.avgDiscountPct || 0}%`} colour="#f97316" icon="🏷️" />
      </div>

      {/* ── Live Kitchen Status ─────────────────────────────────────────────── */}
      <div style={{ ...card, marginBottom: '1.25rem' }}>
        <div style={secHead}>🍽️ Live Kitchen Status</div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, 1fr)', gap: isMobile ? '0.6rem' : '1rem' }}>
          {['RAISED', 'PREPARING', 'READY'].map(s => (
            <div key={s} style={{ background: KOT_STATUS_COLOUR[s] + '18', border: `2px solid ${KOT_STATUS_COLOUR[s]}`, borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: KOT_STATUS_COLOUR[s] }}>{activeKots[s] || 0}</div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: KOT_STATUS_COLOUR[s], textTransform: 'uppercase', letterSpacing: '0.05em' }}>{KOT_STATUS_LABEL[s]}</div>
              <div style={{ fontSize: '0.7rem', color: C.textMuted, marginTop: '0.2rem' }}>
                {s === 'RAISED' && 'Waiting for kitchen'}
                {s === 'PREPARING' && 'Currently cooking'}
                {s === 'READY' && 'Ready to serve guest'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Hourly Activity + Revenue History ──────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>

        {/* Hourly heatmap */}
        <div style={card}>
          <div style={secHead}>⏰ Hourly Order Activity (Today)</div>
          {hourly.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: '0.85rem' }}>No orders yet today.</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '80px', marginBottom: '0.3rem' }}>
                {Array.from({ length: 24 }, (_, h) => {
                  const row = hourly.find(r => r.hour === h);
                  const cnt = row?.order_count || 0;
                  const ht = Math.max(2, Math.round((cnt / maxHourlyCount) * 70));
                  const intensity = cnt === 0 ? 'var(--theme-border)' : cnt < maxHourlyCount * 0.33 ? '#93c5fd' : cnt < maxHourlyCount * 0.66 ? '#3b82f6' : '#1d4ed8';
                  return (
                    <div key={h} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                      <div title={`${h}:00 — ${cnt} orders · ${fmtMoney(row?.amount || 0)}`}
                        style={{ width: '100%', height: `${ht}px`, background: intensity, borderRadius: '2px 2px 0 0', cursor: 'default' }} />
                      {h % 4 === 0 && <div style={{ fontSize: '0.45rem', color: C.textMuted }}>{h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`}</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.72rem', color: C.textMuted, marginTop: '0.4rem', flexWrap: 'wrap' }}>
                {(() => {
                  const peakHour = hourly.reduce((a, b) => (b.order_count > (a?.order_count || 0) ? b : a), null);
                  return peakHour ? <span>Peak: <strong style={{ color: C.text }}>{peakHour.hour}:00 ({peakHour.order_count} orders)</strong></span> : null;
                })()}
                <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <span style={{ width: '10px', height: '10px', background: '#93c5fd', borderRadius: '2px', display: 'inline-block' }} /> Low
                  <span style={{ width: '10px', height: '10px', background: '#3b82f6', borderRadius: '2px', display: 'inline-block' }} /> Med
                  <span style={{ width: '10px', height: '10px', background: '#1d4ed8', borderRadius: '2px', display: 'inline-block' }} /> High
                </span>
              </div>
            </>
          )}
        </div>

        {/* Revenue history */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <div style={{ ...secHead, marginBottom: 0, borderBottom: 'none' }}>📈 Revenue History</div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {['7d', '28d'].map(v => (
                <button key={v} onClick={() => setHistView(v)}
                  style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', border: `1px solid ${C.border}`, background: histView === v ? C.primary : '#fff', color: histView === v ? '#fff' : C.text, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          {displayHistory.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: '0.85rem' }}>No history available.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '90px' }}>
              {displayHistory.map((r, i) => {
                const val = Number(r.amount || 0);
                const ht = Math.max(2, Math.round((val / maxHistAmt) * 76));
                const isToday = r.day?.toString().slice(0, 10) === todayIso();
                return (
                  <div key={i} style={{ flex: 1, minWidth: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                    <div title={`${r.day?.toString().slice(0, 10)}: ${fmtMoney(val)}`}
                      style={{ width: '100%', height: `${ht}px`, background: isToday ? C.accent : '#bfdbfe', borderRadius: '2px 2px 0 0', cursor: 'default' }} />
                    {i % Math.ceil(displayHistory.length / 7) === 0 && (
                      <div style={{ fontSize: '0.45rem', color: C.textMuted, whiteSpace: 'nowrap' }}>
                        {fmtDateDayMonth(r.day)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', fontSize: '0.72rem', color: C.textMuted, flexWrap: 'wrap' }}>
            <span>Avg/day: <strong style={{ color: C.text }}>{fmtMoney(history.length ? history.reduce((s, r) => s + Number(r.amount || 0), 0) / history.length : 0)}</strong></span>
            <span>Peak: <strong style={{ color: C.text }}>{fmtMoney(Math.max(...history.map(r => Number(r.amount || 0)), 0))}</strong></span>
          </div>
        </div>
      </div>

      {/* ── Order Type Mix + Payment Mix ────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>

        {/* Order type mix */}
        <div style={card}>
          <div style={secHead}>📦 Order Type Mix (Today)</div>
          {orderTypeMix.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: '0.85rem' }}>No data.</div>
          ) : orderTypeMix.map((r, i) => {
            const pct = orderTypeTotal > 0 ? ((r.cnt / orderTypeTotal) * 100).toFixed(1) : '0.0';
            const colour = ORDER_TYPE_COLOURS[i % ORDER_TYPE_COLOURS.length];
            return (
              <div key={r.order_type} style={{ marginBottom: '0.6rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.2rem' }}>
                  <span style={{ fontWeight: 600, color: C.text }}>{ORDER_TYPE_LABEL[r.order_type] || r.order_type}</span>
                  <span style={{ color: C.textMuted }}>{r.cnt} orders · {fmtMoney(r.amount)}</span>
                </div>
                <div style={{ height: '8px', background: 'var(--theme-border-light)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: colour, borderRadius: '4px', transition: 'width 0.4s' }} />
                </div>
                <div style={{ fontSize: '0.68rem', color: C.textMuted, marginTop: '0.1rem' }}>{pct}%</div>
              </div>
            );
          })}
        </div>

        {/* Payment mix */}
        <div style={card}>
          <div style={secHead}>💳 Payment Mix (Today)</div>
          {paymentMix.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: '0.85rem' }}>No settled payments yet.</div>
          ) : paymentMix.map((r) => {
            const pct = payTotal > 0 ? ((Number(r.amount) / payTotal) * 100).toFixed(1) : '0.0';
            const colour = PAYMENT_COLOURS[r.payment_mode] || 'var(--theme-text-muted)';
            return (
              <div key={r.payment_mode} style={{ marginBottom: '0.6rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.2rem' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600, color: C.text }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: colour, display: 'inline-block' }} />
                    {r.payment_mode}
                  </span>
                  <span style={{ color: C.textMuted }}>{r.cnt} bills · {fmtMoney(r.amount)}</span>
                </div>
                <div style={{ height: '8px', background: 'var(--theme-border-light)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: colour, borderRadius: '4px', transition: 'width 0.4s' }} />
                </div>
                <div style={{ fontSize: '0.68rem', color: C.textMuted, marginTop: '0.1rem' }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Top Items + Category Mix ─────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>

        {/* Top items */}
        <div style={card}>
          <div style={secHead}>🏆 Top Selling Items (Today)</div>
          {topItems.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: '0.85rem' }}>No items sold yet.</div>
          ) : topItems.map((r, i) => {
            const maxQty = topItems[0]?.total_qty || 1;
            const widthPct = ((r.total_qty / maxQty) * 100).toFixed(0);
            return (
              <div key={r.item_name} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.55rem' }}>
                <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: i === 0 ? '#f59e0b' : i === 1 ? 'var(--theme-text-muted)' : i === 2 ? '#b45309' : C.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, color: i < 3 ? '#fff' : C.textMuted, flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                    <span style={{ fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.item_name}</span>
                    <span style={{ color: C.textMuted, flexShrink: 0, marginLeft: '0.5rem' }}>{fmtNum(r.total_qty)} qty</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.15rem' }}>
                    <div style={{ flex: 1, height: '5px', background: 'var(--theme-border-light)', borderRadius: '3px' }}>
                      <div style={{ height: '100%', width: `${widthPct}%`, background: i === 0 ? C.accent : '#93c5fd', borderRadius: '3px', transition: 'width 0.4s' }} />
                    </div>
                    <span style={{ fontSize: '0.68rem', color: C.textMuted, flexShrink: 0 }}>{fmtMoney(r.total_amount)}</span>
                  </div>
                  <div style={{ fontSize: '0.65rem', color: C.textMuted }}>{r.category_name}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Category mix */}
        <div style={card}>
          <div style={secHead}>🗂️ Category Sales Mix (Today)</div>
          {catMix.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: '0.85rem' }}>No data.</div>
          ) : catMix.map((r, i) => {
            const pct = catTotal > 0 ? ((Number(r.total_amount) / catTotal) * 100).toFixed(1) : '0.0';
            const colour = ORDER_TYPE_COLOURS[i % ORDER_TYPE_COLOURS.length];
            return (
              <div key={r.category_name} style={{ marginBottom: '0.55rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.2rem' }}>
                  <span style={{ fontWeight: 600, color: C.text }}>{r.category_name}</span>
                  <span style={{ color: C.textMuted }}>{fmtNum(r.total_qty)} qty · {fmtMoney(r.total_amount)}</span>
                </div>
                <div style={{ height: '8px', background: 'var(--theme-border-light)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: colour, borderRadius: '4px', transition: 'width 0.4s' }} />
                </div>
                <div style={{ fontSize: '0.68rem', color: C.textMuted, marginTop: '0.1rem' }}>{pct}% of revenue</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Table Utilization + Station Performance ─────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>

        {/* Table utilization */}
        <div style={card}>
          <div style={secHead}>🪑 Table Utilization (Today)</div>
          {tableUtil.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: '0.85rem' }}>No table orders today.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: C.headerBg, color: C.headerText }}>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Table</th>
                    <th style={{ padding: '0.5rem 0.4rem', textAlign: 'center' }}>Orders</th>
                    <th style={{ padding: '0.5rem 0.4rem', textAlign: 'center' }}>Settled</th>
                    <th style={{ padding: '0.5rem 0.4rem', textAlign: 'center' }}>Open</th>
                    <th style={{ padding: '0.5rem 0.6rem', textAlign: 'right' }}>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {tableUtil.map((r, i) => (
                    <tr key={r.table_name} style={{ background: i % 2 === 0 ? 'var(--theme-bg-light)' : '#fff', borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '0.45rem 0.6rem', fontWeight: 600, color: C.text }}>{r.table_name}</td>
                      <td style={{ padding: '0.45rem 0.4rem', textAlign: 'center', color: C.textMuted }}>{r.order_count}</td>
                      <td style={{ padding: '0.45rem 0.4rem', textAlign: 'center', color: C.success, fontWeight: 600 }}>{r.settled_count}</td>
                      <td style={{ padding: '0.45rem 0.4rem', textAlign: 'center', color: r.open_count > 0 ? C.warn : C.textMuted, fontWeight: r.open_count > 0 ? 700 : 400 }}>{r.open_count}</td>
                      <td style={{ padding: '0.45rem 0.6rem', textAlign: 'right', fontWeight: 600, color: C.text }}>{fmtMoney(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Kitchen station performance */}
        <div style={card}>
          <div style={secHead}>🔥 Kitchen Station Performance</div>
          {stationPerf.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: '0.85rem' }}>No KOTs completed today.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: C.headerBg, color: C.headerText }}>
                      <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left' }}>Station</th>
                      <th style={{ padding: '0.5rem 0.4rem', textAlign: 'center' }}>KOTs</th>
                      <th style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>Avg Prep</th>
                      <th style={{ padding: '0.5rem 0.4rem', textAlign: 'right' }}>Avg Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stationPerf.map((r, i) => {
                      const prepColour = r.avg_prep_min <= 10 ? C.success : r.avg_prep_min <= 20 ? C.warn : C.danger;
                      return (
                        <tr key={r.station_name} style={{ background: i % 2 === 0 ? 'var(--theme-bg-light)' : '#fff', borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '0.45rem 0.6rem', fontWeight: 600, color: C.text }}>{r.station_name}</td>
                          <td style={{ padding: '0.45rem 0.4rem', textAlign: 'center', color: C.textMuted }}>{r.kot_count}</td>
                          <td style={{ padding: '0.45rem 0.4rem', textAlign: 'right', fontWeight: 700, color: prepColour }}>{r.avg_prep_min > 0 ? `${r.avg_prep_min} min` : '—'}</td>
                          <td style={{ padding: '0.45rem 0.4rem', textAlign: 'right', color: C.textMuted }}>{r.avg_total_min > 0 ? `${r.avg_total_min} min` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: '0.68rem', color: C.textMuted, marginTop: '0.5rem' }}>
                <span style={{ color: C.success }}>● ≤10 min</span>&nbsp;&nbsp;
                <span style={{ color: C.warn }}>● ≤20 min</span>&nbsp;&nbsp;
                <span style={{ color: C.danger }}>● &gt;20 min</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Forecast ────────────────────────────────────────────────────────── */}
      {forecast.daily?.length > 0 && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ ...secHead, marginBottom: 0, borderBottom: 'none' }}>🔮 Demand Forecast — Next 7 Days</div>
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8rem' }}>
              <span>Avg/Day: <strong style={{ color: C.accent }}>{fmtMoney(forecast.avgPerDay)}</strong></span>
              <span>7-Day Total: <strong style={{ color: C.accent }}>{fmtMoney(forecast.next7DaysAmount)}</strong></span>
            </div>
          </div>
          <MiniBars
            rows={forecast.daily.map(r => ({ ...r, label: fmtWeekdayShort(r.date) }))}
            valueKey="predicted_amount"
            labelKey="label"
            colour={(r, i) => `hsl(${210 + i * 10}, 70%, ${55 + i * 3}%)`}
            height={90}
          />
        </div>
      )}

    </div>
  );
}

export default KotDashboard;
